/* eslint-disable max-lines-per-function */
import { useIsMonatAbgeschlossen } from '@/domain/arbeitszeiten/hooks/useIsMonatAbgeschlossen';
import {
  faEdit,
  faLock,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, Link } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { t } from 'i18next';
import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function ActionCellContentArbeitszeit({ id }: { id: string }) {
  const { currentStandort } = useStandortContext();
  const { year, month, day } = useParams();

  const isMonatAbgeschlossen = useIsMonatAbgeschlossen(
    `${year}-${month}-${day}`,
    currentStandort.id,
  );
  return (
    <span className="flex justify-end">
      <Link
        to={`/arbeitszeiten/uebersicht/${year}/${month}/${day}/sheet/edit/${id}`}>
        <Button
          size="sm"
          variant="default"
          disabled={isMonatAbgeschlossen}
          className="flex items-center gap-2 bg-transparent disabled:bg-white hover:bg-transparent disabled:hover:bg-transparent">
          <FontAwesomeIcon icon={faEdit} className="text-primary-blue-600" />
        </Button>
      </Link>
    </span>
  );
}

export function ActionCellContentAntrag({ id }: { id: string }) {
  const { year, month, day } = useParams();
  const linkToAntrag = `/arbeitszeiten/uebersicht/${year}/${month}/${day}/sheet/antrag/${id}`;
  return (
    <span className="flex justify-end">
      <Link to={linkToAntrag}>
        <Button
          size="sm"
          variant="default"
          className="flex items-center gap-2 bg-transparent disabled:bg-white hover:bg-transparent disabled:hover:bg-transparent">
          <FontAwesomeIcon icon={faEdit} className="text-primary-blue-600 " />
        </Button>
      </Link>
    </span>
  );
}

export function ActionCellContentKonflikt({
  arbeitszeitId,
  disabled = false,
  onDelete,
  showEdit = true,
  deleteInfoText = '',
}: {
  arbeitszeitId?: string;
  disabled?: boolean;
  showEdit?: boolean;
  onDelete?: () => Promise<void>;
  deleteInfoText?: string;
}) {
  const { year, month, day } = useParams();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleDeleteClick = async () => {
    try {
      await onDelete();
    } catch (e) {
      toast({
        title: t('general.genericErrorMessage'),
      });
      Sentry.captureException(e);
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <span className="flex justify-end">
      {showEdit && (
        <Link
          to={`/arbeitszeiten/uebersicht/${year}/${month}/${day}/sheet/edit/${arbeitszeitId}`}>
          <Button
            size="sm"
            variant="icon"
            disabled={disabled}
            className="hover:bg-transparent">
            <FontAwesomeIcon
              icon={disabled ? faLock : faEdit}
              className={` ${
                disabled ? 'text-neutrals-700' : 'text-primary-blue-600'
              }`}
            />
          </Button>
        </Link>
      )}

      <Popover
        open={isConfirmOpen}
        onOpenChange={(open) => {
          setIsConfirmOpen(open);
        }}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            disabled={disabled}
            variant="icon"
            className="hover:bg-transparent">
            <FontAwesomeIcon
              icon={faTrashCan}
              className={`${
                disabled ? 'text-neutrals-700' : 'text-primary-red-400'
              } `}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          className="text-sm flex flex-col gap-2 items-start w-[330px] text-start bg-white">
          <div className="font-semibold">{deleteInfoText}</div>
          <div className="flex gap-2">
            <Button
              className="right-2 transition-colors h-8"
              variant="destructive"
              disabled={disabled}
              onClick={handleDeleteClick}>
              {disabled
                ? t('domain.arbeitszeiten.sheet.arbeitszeit.confirmIsDeleting')
                : t('domain.arbeitszeiten.sheet.arbeitszeit.confirmButton')}
            </Button>
            <Button
              className="right-2 transition-colors px-2.5 h-8"
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}>
              <FontAwesomeIcon icon={faXmark} className="text-lg" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}
